import { DEFAULT_BRAND_KEY, type BrandKey } from '../../../config/brands';
import { BRAND_PROFILES } from '../../../config/brandProfiles';
import type { StatementTotals } from '../../utils/useStatementModel';
import { centsToDollars } from '../../utils/useStatementModel';
import { InfoTip } from './InfoTip';

/**
 * Invoiced-for-the-month totals from the `month_gross_booking` RPC. A different basis
 * from the rest of this page: everything else here is payment-driven (cash received in
 * the month), while this is what was billed for the month whether or not it was paid.
 */
export interface MonthBookingBrand {
  invoice_count: number;
  gross_booked: number;
  collected: number;
  outstanding: number;
  voided_count: number;
  voided_amount: number;
  /**
   * Distinct programs billed. Lives per brand rather than at the top level because it is a
   * distinct count, so unlike the money figures it cannot be summed across brands.
   */
  programs: number;
}

export interface MonthBooking {
  /** All of these span EVERY brand. For one brand's share, read `brands`. */
  invoice_count: number;
  gross_booked: number;
  collected: number;
  outstanding: number;
  voided_count: number;
  voided_amount: number;
  /**
   * One entry per brand that actually billed in the month — a brand with no invoices is
   * absent rather than present-and-zero, since "did not bill" and "billed $0" are different
   * claims. Keyed by `programs.brand_key`.
   */
  brands: Partial<Record<BrandKey, MonthBookingBrand>>;
}

interface KpiStripProps {
  totals: StatementTotals;
  /** Prior month's rent at default rules, for the small delta on the hero tile. */
  priorRentCents?: number;
  /** Invoiced-for-the-month figures; undefined while loading or on failure. */
  booking?: MonthBooking;
  /**
   * The brand this statement covers. Every tile is that brand's: the statement totals arrive
   * already filtered, and Gross booked reads `booking.brands[brand]`, never the all-brand
   * top-level figures, which would sit a merged number beside one brand's rent.
   */
  brand?: BrandKey;
}

function Tile({
  label,
  value,
  sub,
  hero = false,
  tip,
}: {
  label: string;
  value: string;
  sub?: string;
  hero?: boolean;
  tip?: string;
}) {
  return (
    <div
      className={`rounded-xl border px-4 py-3 ${
        hero ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
        <span>{label}</span>
        {tip && <InfoTip text={tip} />}
      </div>
      <div className={`mt-0.5 tabular-nums ${hero ? 'text-2xl font-bold text-gray-900' : 'text-xl font-semibold text-gray-900'}`}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-gray-500 tabular-nums">{sub}</div>}
    </div>
  );
}

/** `$1,234.50` from a dollar-denominated numeric (the booking RPC returns dollars, not cents). */
const dollars = (n: number): string =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Live KPI tiles — recompute as rules and overrides change. */
export function KpiStrip({ totals, priorRentCents, booking, brand = DEFAULT_BRAND_KEY }: KpiStripProps) {
  const brandName = BRAND_PROFILES[brand].shortName;
  // Absent means this brand billed nothing for the month (the RPC omits a brand with no
  // invoices), which is $0 booked -- not "unknown", which is what `booking` undefined means.
  const brandBooking = booking?.brands?.[brand];

  let delta: string | undefined;
  if (priorRentCents !== undefined && priorRentCents !== 0) {
    const diff = totals.rentCents - priorRentCents;
    delta = `${diff >= 0 ? '+' : '−'}$${centsToDollars(Math.abs(diff))} vs prior month`;
  }

  // Booking sub-line: how much of what was billed for the month has come in.
  let bookingSub: string | undefined;
  if (booking) {
    const n = brandBooking?.invoice_count ?? 0;
    const gross = brandBooking?.gross_booked ?? 0;
    const parts = [`${n} invoice${n === 1 ? '' : 's'}`];
    if (gross > 0) {
      parts.push(`${Math.round(((brandBooking?.collected ?? 0) / gross) * 100)}% collected`);
    }
    bookingSub = parts.join(' · ');
  }

  // Payments tile: the count, plus the money those payments actually moved. Refund rows
  // carry negative cents, so this total is already net of them — and can go negative in a
  // heavy-refund month, hence the explicit sign rather than a bare "$-5.00".
  const net = totals.collectionsCents;
  const netLabel = `${net < 0 ? '−$' : '$'}${centsToDollars(Math.abs(net))} net collected`;
  const paymentsSub = [
    netLabel,
    totals.nRefunds > 0 ? `${totals.nRefunds} refund${totals.nRefunds === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <Tile
        hero
        label="Rent owed"
        value={`$${centsToDollars(totals.rentCents)}`}
        sub={delta}
        tip={`What ${brandName} owes for the month: the rent base times the share percent, plus the rent on any add-back or manual adjustment lines. This is the figure the CSV and PDF export.`}
      />
      <Tile
        label="Rent base"
        value={`$${centsToDollars(totals.baseCents)}`}
        tip="The amount the share percent is applied to: gross tuition less the excluded BHCS Cash bonus, plus any add-back lines from rules you switched off and any manual adjustments. Excluded programs are not in it."
      />
      <Tile
        label="Gross tuition"
        value={`$${centsToDollars(totals.grossTuitionCents)}`}
        tip="Tuition allocated from payments received this month, across included programs, before the bonus exclusion. Fees and materials are not counted here."
      />
      <Tile
        label="Gross booked"
        value={booking ? dollars(brandBooking?.gross_booked ?? 0) : '—'}
        sub={bookingSub}
        tip={`Invoiced for this month, paid or not, across every ${brandName} program. Voided invoices are excluded. It does not feed the rent calculation.`}
      />
      <Tile
        label="Bonus excluded"
        value={`−$${centsToDollars(totals.bonusExcludedCents)}`}
        tip="BHCS Cash bonus dollars taken out of the rent base — the promotional top-up BHCS funded itself, not money a family paid. Switch the rule off in Statement rules to add it back."
      />
      <Tile
        label="Payments"
        value={String(totals.nPayments)}
        sub={paymentsSub}
        tip="How many payment rows landed this month across included programs, and the money they moved: tuition plus fees and materials, net of refunds. Refunds are counted separately in the row count."
      />
    </div>
  );
}
